import {
	AutoSidebar,
	SidebarHeader,
	SidebarItem,
	VersionAnchor,
	useIsAddonEnabled,
	useUserPermission,
} from "@dgs/core";
import { ISidebarLink } from "@dgs/core/lib/types/sidebarItem";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Addon } from "../../../shared/types/addon";
import { PermissionName } from "../../../shared/types/permission";
import { config } from "../config";
import { useSidebarData } from "../shared/api/sidebarService";

const StyledAutoSidebar = styled(AutoSidebar)`
	position: relative;
	z-index: ${({ theme }) => theme.zIndices.content.l2};
`;

export const Sidebar: FC = () => {
	const { t } = useTranslation();
	const { permissions } = useUserPermission();
	const { sidebarData } = useSidebarData();
	const applicationsForSidebar = sidebarData?.applications;
	const hasEvents = sidebarData?.hasEvents ?? false;
	const isContactDatabaseEnabled = useIsAddonEnabled(Addon.CONTACT_DATABASE);
	const isAppEnabled = useIsAddonEnabled(Addon.APP);
	const isCMSEnabled = useIsAddonEnabled(Addon.CMS);
	const arePreferencesEnabled = useIsAddonEnabled(Addon.PREFERENCES);
	const filterItems = useMemo<ISidebarLink<PermissionName>[]>(
		() =>
			(sidebarData?.contactFilters ?? []).map((filter) => {
				return {
					src: "/contacts.svg",
					key: filter.name,
					to: `/contacts/filter/${filter.id}/?contactFilterId=${filter.id}`,
					permissions: ["contact.read"],
					hide: !isContactDatabaseEnabled,
				};
			}),
		[sidebarData?.contactFilters, isContactDatabaseEnabled],
	);

	const sidebarItems = useMemo<SidebarItem<PermissionName>[]>(() => {
		const sideBarItems: SidebarItem<PermissionName>[] = [
			{
				icon: "dashboard",
				key: t("Dashboard"),
				permissions: ["dashboard.read"],
				to: "/dashboard",
			},
			{
				icon: "calendar",
				key: t("Events"),
				permissions: ["admin.login"],
				to: "/events",
				hide: !hasEvents,
				children: [
					{
						src: "/event_available.svg",
						key: t("Active"),
						permissions: ["admin.login"],
						to: "/events/active",
						hide: !hasEvents,
					},
					{
						src: "/calendar_clock.svg",
						key: t("Archived"),
						permissions: ["admin.login"],
						to: "/events/archived",
						hide: !hasEvents,
					},
					{
						src: "/calendar_month.svg",
						key: t("Overview"),
						permissions: ["admin.login"],
						to: "/events/overview/calendar",
						hide: !hasEvents,
					},
					{
						src: "/event_request.svg",
						key: t("Participation requests"),
						permissions: ["admin.login"],
						to: "/events/requests/open",
						hide: !hasEvents,
					},
				],
			},
			{
				icon: "layers",
				key: t("Content management"),
				permissions: ["block.read"],
				hide: !isCMSEnabled,
				children: [
					{
						icon: "layers",
						key: t("Pages"),
						permissions: ["block.read"],
						to: "/content-management/blocks/pages",
					},
					{
						icon: "inventory",
						key: t("Blocks"),
						permissions: ["block.read"],
						to: "/content-management/blocks/all",
					},
					{
						icon: "images",
						key: t("Images"),
						permissions: ["image.read"],
						to: "/content-management/images",
					},
					{
						icon: "files",
						key: t("Files"),
						permissions: ["file.read"],
						to: "/content-management/files",
					},
				],
			},
			{
				icon: "menu",
				key: t("Menu"),
				permissions: ["menuItem.read"],
				to: "/menu",
			},
			{
				src: "/patient_list.svg",
				key: t("Contact database"),
				permissions: [
					"contact.read",
					"company.read",
					"distributionList.read",
					"dataField.read",
					"tag.read",
					"category.read",
				],
				hide: !isContactDatabaseEnabled,
				children: [
					{
						src: "/contacts.svg",
						key: t("Contacts"),
						permissions: ["contact.read"],
						to: "/contacts",
						hide: !isContactDatabaseEnabled,
						children: [
							{
								src: "/contacts.svg",
								key: t("Active"),
								to: `/contacts/active`,
								permissions: ["contact.read"],
								hide: !isContactDatabaseEnabled,
							},
							{
								src: "/contacts.svg",
								key: t("Archived"),
								to: `/contacts/archived`,
								permissions: ["contact.read"],
								hide: !isContactDatabaseEnabled,
							},
							...filterItems,
						],
					},
					{
						src: "/companies.svg",
						key: t("Companies"),
						permissions: ["company.read"],
						to: "/companies",
						hide: !isContactDatabaseEnabled,
						children: [
							{
								src: "/companies.svg",
								key: t("Active"),
								permissions: ["company.read"],
								to: "/companies/active",
								hide: !isContactDatabaseEnabled,
							},
							{
								src: "/companies.svg",
								key: t("Archived"),
								permissions: ["company.read"],
								to: "/companies/archived",
								hide: !isContactDatabaseEnabled,
							},
						],
					},
					{
						icon: "layer_group",
						key: t("Contact types"),
						permissions: ["contactType.read"],
						to: "/contact-types",
						hide: !isContactDatabaseEnabled,
					},
					{
						src: "/groups.svg",
						key: t("Distribution lists"),
						permissions: ["distributionList.read"],
						to: "/distribution-lists",
						hide: !isContactDatabaseEnabled,
					},
					{
						icon: "layers",
						key: t("Contact views"),
						permissions: ["contact.read"],
						to: "/contact-views",
						hide: !isContactDatabaseEnabled,
					},
					{
						icon: "filter",
						key: t("Contact filters"),
						permissions: ["contact.read"],
						to: "/contact-filters",
						hide: !isContactDatabaseEnabled,
					},
					{
						icon: "calendar",
						key: t("Data fields"),
						permissions: ["dataField.read"],
						to: "/data-fields",
						hide: !isContactDatabaseEnabled,
					},
					{
						icon: "tags",
						key: t("Tags"),
						permissions: ["tag.read"],
						to: "/tags",
					},
					{
						icon: "sidebar_group",
						key: t("Categories"),
						permissions: ["category.read"],
						to: "/categories",
					},
					{
						src: "/person_check.svg",
						key: t("Contact update campaigns"),
						permissions: ["contactUpdateInquiry.read"],
						to: "/contact-update-campaigns",
					},
				],
			},
		];

		if (applicationsForSidebar) {
			const mailCoachSrc = "/mail.svg";
			const carFleetSrc = "/car.svg";

			const applicationForSidebarItems: SidebarItem<PermissionName>[] = [];

			if (applicationsForSidebar.carFleets.length === 1) {
				applicationForSidebarItems.push({
					src: carFleetSrc,
					key: t("Car fleet"),
					permissions: ["application.read", "application.readOwn"],
					href: applicationsForSidebar.carFleets[0].adminUrl,
				});
			}

			if (applicationsForSidebar.carFleets.length > 1) {
				applicationForSidebarItems.push({
					src: carFleetSrc,
					key: t("Car fleets"),
					permissions: ["application.read", "application.readOwn"],
					children: applicationsForSidebar.carFleets.map((carFleet) => ({
						src: carFleetSrc,
						key: carFleet.subdomain,
						permissions: ["application.read", "application.readOwn"],
						href: carFleet.adminUrl,
					})),
				});
			}

			if (applicationsForSidebar.mailcoaches.length === 1) {
				applicationForSidebarItems.push({
					src: mailCoachSrc,
					key: t("Newsletter"),
					permissions: ["application.read", "application.readOwn"],
					opensInNewTab: true,
					href: applicationsForSidebar.mailcoaches[0].adminUrl,
				});
			}

			if (applicationsForSidebar.mailcoaches.length > 1) {
				applicationForSidebarItems.push({
					src: "/mails.svg",
					key: t("Newsletters"),
					permissions: ["application.read", "application.readOwn"],
					children: applicationsForSidebar.mailcoaches.map((mailcoach) => ({
						src: mailCoachSrc,
						key: mailcoach.subdomain,
						permissions: ["application.read", "application.readOwn"],
						opensInNewTab: true,
						href: mailcoach.adminUrl,
					})),
				});
			}

			sideBarItems.push(...applicationForSidebarItems);
		}

		sideBarItems.push(
			{
				icon: "analytics",
				key: t("Campaigns"),
				permissions: ["campaign.read"],
				to: "/campaigns",
			},
			{
				src: "/clipboard_list.svg",
				key: t("Clippings"),
				permissions: ["clipping.read"],
				to: "/clippings",
				hide: !isContactDatabaseEnabled,
			},
			{
				src: "/delete_sweep.svg",
				key: t("Deletion requests"),
				permissions: ["deletionRequest.read"],
				to: "/deletion-requests",
			},
			{
				icon: "text_snippet",
				key: t("Feedback"),
				permissions: ["feedback.read"],
				to: "/feedbacks",
			},
			{
				icon: "file_download",
				key: t("Exports"),
				permissions: ["export.read"],
				to: "/exports",
			},
		);

		if (isAppEnabled) {
			sideBarItems.push({
				icon: "smartphone",
				key: t("App"),
				permissions: ["appNotification.read", "bottomNavBarItems.read"],
				children: [
					{
						src: "/transition_push.svg",
						key: t("Notification"),
						permissions: ["appNotification.send"],
						to: "/app/notifications",
					},
					{
						icon: "tabs",
						key: t("Nav bar"),
						permissions: ["bottomNavBarItems.read"],
						to: "/app/nav-bar",
					},
					{
						icon: "settings",
						key: t("Settings"),
						permissions: ["moduleSettings.read"],
						to: "/app/settings",
					},
				],
			});
		}

		sideBarItems.push({
			src: "/monitoring.svg",
			key: t("Analytics"),
			permissions: ["analytics.read", "budgetAnalytics.read"],
			children: [
				{
					icon: "calendar",
					key: t("Events"),
					permissions: ["analytics.read"],
					to: "/analytics/events",
				},
				{
					src: "/savings.svg",
					key: t("Budget"),
					permissions: ["budgetAnalytics.read"],
					to: "/analytics/budget",
				},
				{
					icon: "text_snippet",
					key: t("Feedback"),
					permissions: ["analytics.read"],
					to: "/analytics/feedback",
				},
			],
		});

		sideBarItems.push({
			icon: "settings",
			key: t("Event settings"),
			permissions: ["eventSettings.read"],
			children: [
				{
					src: "/domain_icon.svg",
					key: t("Organizational units"),
					permissions: ["organizationalUnits.read"],
					to: "/event-settings/organizational-units",
				},
				{
					icon: "cards",
					key: t("Event categories"),
					permissions: ["eventCategory.read"],
					to: "/event-settings/event-categories",
				},
				{
					src: "/target_group.svg",
					key: t("Target groups"),
					permissions: ["targetGroup.read"],
					to: "/event-settings/target-groups",
				},
				{
					icon: "bookmark",
					key: t("Regions"),
					permissions: ["region.read"],
					to: "/event-settings/regions",
				},
				{
					icon: "marker",
					key: t("Locations"),
					permissions: ["location.read"],
					to: "/event-settings/locations",
				},
				{
					src: "/industry.svg",
					key: t("Industries"),
					permissions: ["industry.read"],
					to: "/event-settings/industries",
				},
				{
					src: "/contact_phone.svg",
					key: t("Organizers"),
					permissions: ["organizers.read"],
					to: "/event-settings/organizers",
				},
				{
					src: "/event_type.svg",
					key: t("Event types"),
					permissions: ["eventTypes.read"],
					to: "/event-settings/event-types",
				},
				{
					icon: "cards",
					key: t("Cost categories"),
					permissions: ["costCategory.read"],
					to: "/event-settings/cost-categories",
				},
				{
					icon: "cards",
					key: t("User responsibilities"),
					permissions: ["userResponsibility.read"],
					to: "/event-settings/user-responsibilities",
				},
			],
		});

		sideBarItems.push({
			icon: "settings",
			key: t("Settings"),
			permissions: [
				"user.read",
				"role.read",
				"userGroup.read",
				"application.read",
				"application.readOwn",
				"tenant.read",
				"consent.read",
				"preference.read",
				"contactDatabaseSettings.read",
				"configurations.read",
				"accessToken.read",
				"integration.read",
			],
			children: [
				{
					icon: "settings",
					key: t("General"),
					permissions: ["configurations.read"],
					to: "/settings",
				},
				{
					icon: "id_card",
					key: t("User onboarding"),
					permissions: ["userOnboarding.read"],
					to: "/user-onboarding",
				},
				{
					icon: "id_card",
					key: t("Users"),
					permissions: ["user.read"],
					to: "/users",
				},
				{
					icon: "lock_open",
					key: t("Roles"),
					permissions: ["role.read"],
					to: "/roles",
				},
				{
					icon: "group",
					key: t("User groups"),
					permissions: ["userGroup.read"],
					to: "/user-groups",
				},
				{
					icon: "dashboard",
					key: t("Applications"),
					permissions: ["application.read", "application.readOwn"],
					to: "/applications",
				},
				{
					src: "/switch_account.svg",
					key: t("Tenants"),
					permissions: ["tenant.read"],
					to: "/tenants",
				},
				{
					icon: "check",
					key: t("Consents"),
					permissions: ["consent.read"],
					to: "/consents",
				},
				{
					icon: "check",
					key: t("Preferences"),
					permissions: ["preference.read"],
					to: "/preferences",
					hide: !arePreferencesEnabled,
				},
				{
					src: "/translate.svg",
					key: t("Product translations"),
					permissions: ["productTranslations.read"],
					to: "/product-translations",
				},
				{
					icon: "footer",
					key: t("Footer links"),
					permissions: ["footerLink.read"],
					to: "/footer-links",
				},
				{
					src: "/patient_list.svg",
					key: t("Contact database"),
					permissions: ["contactDatabaseSettings.read"],
					to: "/contact-settings",
					showAddonBanner: !isContactDatabaseEnabled,
				},
				{
					icon: "person",
					key: t("Profile settings"),
					permissions: ["profileSettings.read"],
					to: "/profile-settings",
				},
				{
					icon: "lock_open",
					key: t("Access tokens"),
					permissions: ["accessToken.read"],
					to: "/access-tokens",
				},
				{
					icon: "settings",
					key: t("Integrations"),
					permissions: ["integration.read"],
					to: "/integrations",
				},
			],
		});

		return sideBarItems;
	}, [
		applicationsForSidebar,
		arePreferencesEnabled,
		filterItems,
		hasEvents,
		isAppEnabled,
		isCMSEnabled,
		isContactDatabaseEnabled,
		t,
	]);

	return (
		<StyledAutoSidebar
			header={<SidebarHeader href={config.sidebarHeaderHref} />}
			sidebarItems={sidebarItems}
			footer={<VersionAnchor target="_blank" />}
			permissions={permissions}
		/>
	);
};
